import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { Send, Loader2 } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [devToken, setDevToken] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setDone(true);
      if (data?.dev_token) setDevToken(data.dev_token);
      toast.success("If the email exists, a reset link has been sent.");
    } catch (err) {
      toast.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 justify-center mb-8">
          <div className="w-9 h-9 rounded-md bg-brand grid place-items-center text-white">
            <Send className="w-4 h-4" />
          </div>
          <span className="font-heading text-xl font-semibold text-slate-900">FlowSend</span>
        </Link>
        <Card className="border border-slate-200 shadow-none">
          <CardContent className="p-7">
            <h1 className="font-heading text-2xl font-semibold tracking-tight text-slate-900">
              Reset your password
            </h1>
            <p className="text-sm text-slate-500 mt-1 mb-6">
              Enter your email and we'll send you a reset link.
            </p>
            {done ? (
              <div className="space-y-3">
                <div className="p-3 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm">
                  If <span className="font-mono">{email}</span> exists, a reset email is on the way.
                </div>
                {devToken && (
                  <div className="p-3 rounded-md bg-amber-50 border border-amber-200 text-amber-900 text-xs">
                    <div className="font-semibold mb-1">Dev mode reset token:</div>
                    <code className="break-all">{devToken}</code>
                    <div className="mt-2">
                      Visit <code>/reset-password?token={devToken.slice(0, 8)}…</code> (production would email this).
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="email" className="text-xs font-semibold tracking-wider uppercase text-slate-600">
                    Email
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="forgot-email-input"
                    required
                    className="mt-1.5"
                  />
                </div>
                <Button
                  type="submit"
                  data-testid="forgot-submit-button"
                  disabled={loading}
                  className="w-full bg-brand hover:bg-brand-dark text-white"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send reset link"}
                </Button>
              </form>
            )}
            <p className="text-center text-sm text-slate-500 mt-6">
              Remember your password?{" "}
              <Link to="/login" className="text-brand-dark font-medium hover:underline">
                Sign in
              </Link>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
